import {
  ExitIcon,
  MagnifyingGlassIcon,
  ReloadIcon,
} from "@radix-ui/react-icons";
import { Button, Dialog, Flex, Text, Theme } from "@radix-ui/themes";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const STAFF_TAP_COUNT = 10;
const STAFF_TAP_MAX_GAP_MS = 2_000;
const STAFF_MENU_IDLE_TIMEOUT_MS = 10_000;
const PINCH_ZOOM_TIMEOUT_MS = 10_000;

type DebugMenuContextValue = { openDebugMenu: () => void };
const DebugMenuContext = createContext<DebugMenuContextValue | null>(null);

type DebugMenuProviderProps = {
  children: ReactNode;
  locked?: boolean;
  onStaffAccess?: () => void;
  onLogout?: () => void | Promise<void>;
  onReconfigure?: () => void;
};

export function DebugMenuProvider({
  children,
  locked = false,
  onStaffAccess,
  onLogout,
  onReconfigure,
}: DebugMenuProviderProps) {
  const [open, setOpen] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [pinchZoomEnabled, setPinchZoomEnabled] = useState(false);
  const idleTimerRef = useRef<number>();
  const pinchZoomTimerRef = useRef<number>();
  const previousViewportContentRef = useRef<string | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const resetIdleTimer = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(close, STAFF_MENU_IDLE_TIMEOUT_MS);
  }, [close]);

  const openDebugMenu = useCallback(() => {
    if (locked) {
      onStaffAccess?.();
      return;
    }
    setOpen(true);
  }, [locked, onStaffAccess]);

  const disablePinchZoom = useCallback(() => {
    window.clearTimeout(pinchZoomTimerRef.current);
    document.documentElement.classList.remove("debug-pinch-zoom");
    setPinchZoomEnabled(false);
    const viewport = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]',
    );
    if (viewport && previousViewportContentRef.current !== null) {
      viewport.setAttribute("content", previousViewportContentRef.current);
      previousViewportContentRef.current = null;
    }
  }, []);

  const enablePinchZoom = useCallback(() => {
    const viewport = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]',
    );
    if (viewport && previousViewportContentRef.current === null) {
      previousViewportContentRef.current = viewport.getAttribute("content");
    }
    viewport?.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes",
    );
    document.documentElement.classList.add("debug-pinch-zoom");
    setPinchZoomEnabled(true);
    window.clearTimeout(pinchZoomTimerRef.current);
    pinchZoomTimerRef.current = window.setTimeout(
      disablePinchZoom,
      PINCH_ZOOM_TIMEOUT_MS,
    );
  }, [disablePinchZoom]);

  const handleLogout = async () => {
    if (!onLogout) return;
    setLogoutPending(true);
    try {
      await onLogout();
      close();
    } finally {
      setLogoutPending(false);
    }
  };

  const contextValue = useMemo(() => ({ openDebugMenu }), [openDebugMenu]);

  useEffect(() => {
    if (!open) {
      window.clearTimeout(idleTimerRef.current);
      return;
    }
    resetIdleTimer();
    return () => window.clearTimeout(idleTimerRef.current);
  }, [open, resetIdleTimer]);

  useEffect(() => disablePinchZoom, [disablePinchZoom]);

  return (
    <DebugMenuContext.Provider value={contextValue}>
      {children}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Content
          width="440px"
          className="!bg-black !rounded-3xl !p-5"
          onPointerDown={resetIdleTimer}
          onPointerMove={resetIdleTimer}
          onKeyDown={resetIdleTimer}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Theme appearance="dark" className="rounded-2xl">
            <Flex direction="column" gap="5" className="w-[400px] p-4">
              <Flex justify="between" align="center">
                <Text weight="bold" size="6">
                  Staff mode
                </Text>
                <Button size="4" variant="ghost" color="gray" onClick={close}>
                  Close
                </Button>
              </Flex>
              <Button
                size="4"
                variant="surface"
                color="gray"
                onClick={() => window.location.reload()}
              >
                <ReloadIcon /> Reload
              </Button>
              {onReconfigure && (
                <Button
                  size="4"
                  variant="surface"
                  color="gray"
                  onClick={() => {
                    close();
                    onReconfigure();
                  }}
                >
                  Reconfigure kiosk
                </Button>
              )}
              {onLogout && (
                <Button
                  size="4"
                  variant="surface"
                  color="gray"
                  loading={logoutPending}
                  onClick={handleLogout}
                >
                  <ExitIcon /> Logout
                </Button>
              )}
              <Button
                size="4"
                variant="surface"
                color="gray"
                onClick={enablePinchZoom}
              >
                <MagnifyingGlassIcon />
                {pinchZoomEnabled
                  ? "Pinch zoom enabled"
                  : "Enable pinch zoom for 10 seconds"}
              </Button>
            </Flex>
          </Theme>
        </Dialog.Content>
      </Dialog.Root>
    </DebugMenuContext.Provider>
  );
}

export function DebugLogoTrigger({ children }: { children: ReactNode }) {
  const menu = useContext(DebugMenuContext);
  const tapCountRef = useRef(0);
  const resetTimerRef = useRef<number>();

  const handleTap = () => {
    if (!menu) return;
    window.clearTimeout(resetTimerRef.current);
    tapCountRef.current += 1;
    if (tapCountRef.current >= STAFF_TAP_COUNT) {
      tapCountRef.current = 0;
      menu.openDebugMenu();
      return;
    }
    resetTimerRef.current = window.setTimeout(() => {
      tapCountRef.current = 0;
    }, STAFF_TAP_MAX_GAP_MS);
  };

  useEffect(() => () => window.clearTimeout(resetTimerRef.current), []);

  return (
    <button
      type="button"
      aria-label="Peky logo"
      className="inline-flex cursor-default touch-manipulation active:bg-gray-200"
      onPointerUp={handleTap}
    >
      <span className="pointer-events-none select-none">{children}</span>
    </button>
  );
}
